import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ExternalLink, Search, X, ChevronRight, Sparkles } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../hooks/useRole';
import { useReadOnly } from '../hooks/useReadOnly';
import { workOrderService, customerService, userService, getWorkOrderStatusLabel } from '../api';
import { Button, ThemedText, ActionMenu, ConfirmDialog, SkeletonTable, EmptyState, ErrorState, SlidePanel, GenerateDemoDataDialog } from '../components';
import { extractItems } from '../utils/responseUtils';
import { formatDateOnly } from '../utils/dateUtils';
import { WorkOrderStatusColors, withOpacity } from '../constants/statusColors';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { ModalOverlay as Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { SearchContainer, StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { FormGroup, FormLabel as Label, StyledSelect as Select } from '../components/FormStyles';

const TableContainer = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const Th = styled.th`
  text-align: left;
  padding: ${props => props.theme.spacing.md};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  border-left: 3px solid ${props => props.$selected ? props.theme.colors.tint : 'transparent'};
  transition: background-color 0.15s ease, border-left-color 0.15s ease;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.tint}08;
    border-left-color: ${props => props.theme.colors.tint};
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.$selected && `
    background-color: ${props.theme.colors.tint}08;
  `}
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  transition: filter 0.15s ease;
  display: inline-block;
  ${props => {
    const c = WorkOrderStatusColors[props.status] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}

  &:hover {
    filter: brightness(1.1);
  }
`;


// Slide panel detail section styles
const DetailSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const DetailLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.muted};
  margin: 0 0 4px 0;
`;

const DetailValue = styled.p`
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const resolveData = extractItems;

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];

const STATUS_OPTIONS = ['Open', 'InProgress', 'OnHold', 'Completed', 'Cancelled'];

const WorkOrdersPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const { user } = useAuth();
  const readOnly = useReadOnly();
  const [isDemoDataOpen, setIsDemoDataOpen] = useState(false);
  // "Generate Demo Data" is for demo-org builders only; read-only viewers would 403 on commit.
  const canGenerateDemoData = user?.isDemoOrg === true && !readOnly;
  const [workOrders, setWorkOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [panelWoId, setPanelWoId] = useState(null);
  const [panelWo, setPanelWo] = useState(null);

  const [statusFormData, setStatusFormData] = useState({
    status: '',
  });

  useEffect(() => {
    fetchWorkOrders();
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (!panelWoId) {
      setPanelWo(null);
      return;
    }
    workOrderService.getWorkOrderById(panelWoId)
      .then(data => setPanelWo(data))
      .catch(() => setPanelWo(null));
  }, [panelWoId]);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const params = {};
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (priorityFilter) {
        params.priority = priorityFilter;
      }
      const response = await workOrderService.getWorkOrders(params);
      const data = resolveData(response);
      setWorkOrders(data);
    } catch (error) {
      console.error('Failed to fetch work orders:', error);
      setFetchError(error?.response?.data?.message || 'Failed to load work orders. Please check your connection and try again.');
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [customersResponse, usersResponse] = await Promise.all([
        customerService.getCustomers(),
        userService.getUsers({}),
      ]);
      setCustomers(resolveData(customersResponse));
      setUsers(resolveData(usersResponse));
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();

    try {
      await workOrderService.updateWorkOrderStatus(selectedWorkOrder.id, { status: statusFormData.status });
      toast.success('Work order status updated successfully');
      setIsStatusModalOpen(false);
      setSelectedWorkOrder(null);
      setStatusFormData({ status: '' });
      fetchWorkOrders();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDeleteWorkOrder = async (id) => {
    try {
      await workOrderService.deleteWorkOrder(id);
      toast.success('Work order deleted successfully');
      setDeleteConfirmId(null);
      fetchWorkOrders();
    } catch (error) {
      console.error('Failed to delete work order:', error);
      toast.error(error.response?.data?.message || 'Failed to delete work order');
    }
  };

  const openStatusModal = (wo) => {
    setSelectedWorkOrder(wo);
    setStatusFormData({ status: wo.status || 'Open' });
    setIsStatusModalOpen(true);
  };


  const getCustomerName = (wo) => {
    if (wo.customerName) return wo.customerName;
    if (wo.customer) return wo.customer.name || wo.customer.companyName || '';
    const customer = customers.find(c => c.id === wo.customerId);
    return customer ? (customer.name || customer.companyName || '') : '-';
  };

  const getAssignedToName = (wo) => {
    // Use assignments array if available (multi-user)
    if (wo.assignments?.length) {
      const names = wo.assignments.map(a => a.userName || '').filter(Boolean);
      if (names.length <= 2) return names.join(', ') || '-';
      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
    }
    // Backward compat fallbacks
    if (wo.assignedToName) return wo.assignedToName;
    if (wo.assignedToUser) return `${wo.assignedToUser.firstName || ''} ${wo.assignedToUser.lastName || ''}`.trim();
    const user = users.find(u => u.id === wo.assignedToUserId);
    return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '-';
  };

  const filteredWorkOrders = workOrders.filter(wo => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    const woNumber = wo.workOrderNumber || wo.woNumber || '';
    const title = wo.title || '';
    const custName = getCustomerName(wo);
    return (
      woNumber.toString().toLowerCase().includes(term) ||
      title.toLowerCase().includes(term) ||
      custName.toLowerCase().includes(term)
    );
  });

  return (
    <Layout currentPath="/work-orders">
      <PageContainer theme={theme} style={{ maxWidth: '100%' }}>
        <PageHeader theme={theme} style={{ alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#43474f', fontWeight: 500, marginBottom: 10 }}>
              <span>Operations</span>
              <ChevronRight size={12} />
              <span style={{ color: '#232562', fontWeight: 700 }}>Work Orders</span>
            </div>
            <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1.875rem', fontWeight: 800, color: '#232562', margin: '0 0 6px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Work Orders</h1>
            <p style={{ color: '#43474f', fontSize: '0.875rem', margin: 0 }}>Manage work orders</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canGenerateDemoData && (
              <Button
                variant="secondary"
                onClick={() => setIsDemoDataOpen(true)}
                title="Generate realistic demo work orders"
              >
                <Sparkles size={14} /> Generate Demo Data
              </Button>
            )}
            {isAdmin() && (
              <Button onClick={() => navigate('/work-orders/new')}>
                + New Work Order
              </Button>
            )}
          </div>
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer theme={theme}>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search by WO#, title, customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            theme={theme}
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{getWorkOrderStatusLabel(s)}</option>
            ))}
          </Select>
          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            theme={theme}
          >
            <option value="">All Priorities</option>
            {PRIORITY_OPTIONS.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
        </SearchContainer>

        <TableContainer theme={theme}>
          {loading ? (
            <SkeletonTable rows={5} cols={8} theme={theme} />
          ) : fetchError ? (
            <ErrorState
              theme={theme}
              title="Failed to load work orders"
              message={fetchError}
              onRetry={fetchWorkOrders}
            />
          ) : filteredWorkOrders.length === 0 ? (
            <EmptyState
              theme={theme}
              icon={<ClipboardList size={48} />}
              title="No work orders found"
              description={searchTerm || statusFilter || priorityFilter
                ? 'Try adjusting your filters or search term'
                : 'Create your first work order to start tracking field work'}
              action={!searchTerm && !statusFilter && !priorityFilter && isAdmin()
                ? { label: '+ New Work Order', onClick: () => navigate('/work-orders/new') }
                : undefined}
            />
          ) : (
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>WO#</Th>
                  <Th theme={theme}>Title</Th>
                  <Th theme={theme}>Customer</Th>
                  <Th theme={theme}>Status</Th>
                  <Th theme={theme}>Priority</Th>
                  <Th theme={theme}>Assigned To</Th>
                  <Th theme={theme}>Dates</Th>
                  <Th theme={theme} style={{ width: '50px' }}></Th>
                </tr>
              </Thead>
              <Tbody>
                {filteredWorkOrders.map(wo => (
                  <Tr
                    key={wo.id}
                    theme={theme}
                    $selected={panelWoId === wo.id}
                    onClick={() => setPanelWoId(wo.id)}
                  >
                    <Td theme={theme}>
                      <ThemedText type="defaultSemiBold" style={{ color: theme.colors.tint }}>
                        {wo.workOrderNumber || wo.woNumber || wo.id}
                      </ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{wo.title}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{getCustomerName(wo)}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <StatusBadge theme={theme} status={wo.status}>
                        {getWorkOrderStatusLabel(wo.status)}
                      </StatusBadge>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{wo.priority || '-'}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{getAssignedToName(wo)}</ThemedText>
                    </Td>
                    <Td theme={theme} style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ lineHeight: 1.4 }}>
                        <span style={{ color: theme.colors.muted, fontSize: '0.7rem' }}>Service: </span>
                        <span style={{ fontSize: '0.85rem' }}>{formatDateOnly(wo.serviceDate)}</span>
                        <br />
                        <span style={{ color: theme.colors.muted, fontSize: '0.7rem' }}>Due: </span>
                        <span style={{ fontSize: '0.85rem' }}>{formatDateOnly(wo.dueDate)}</span>
                      </div>
                    </Td>
                    <Td theme={theme} onClick={(e) => e.stopPropagation()}>
                      <ActionMenu actions={[
                        ...(isAdmin() ? [{ label: 'Edit', onClick: () => navigate(`/work-orders/${wo.id}/edit`) }] : []),
                        { label: 'Change Status', onClick: () => openStatusModal(wo) },
                        ...(isAdmin() ? [{ label: 'Delete', onClick: () => setDeleteConfirmId(wo.id), variant: 'danger' }] : []),
                      ]} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>

        {/* Status Change Modal */}
        <Modal theme={theme} isOpen={isStatusModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Change Status</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsStatusModalOpen(false); setSelectedWorkOrder(null); setStatusFormData({ status: '' }); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleUpdateStatus}>
              <ModalBody theme={theme}>
                {selectedWorkOrder && (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Changing status for: {selectedWorkOrder.title}
                  </ThemedText>
                )}
                <FormGroup theme={theme}>
                  <Label theme={theme}>Status</Label>
                  <Select
                    value={statusFormData.status}
                    onChange={(e) => setStatusFormData({ status: e.target.value })}
                    theme={theme}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{getWorkOrderStatusLabel(s)}</option>
                    ))}
                  </Select>
                </FormGroup>
              </ModalBody>

              <ModalFooter theme={theme}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setIsStatusModalOpen(false); setSelectedWorkOrder(null); setStatusFormData({ status: '' }); }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update Status
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Work Order"
          message="Are you sure you want to delete this work order? This action cannot be undone."
          onConfirm={() => handleDeleteWorkOrder(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      </PageContainer>

      <SlidePanel
        isOpen={panelWoId !== null}
        onClose={() => setPanelWoId(null)}
        title={panelWo ? (panelWo.title || `WO #${panelWo.workOrderNumber || panelWo.woNumber}`) : 'Work Order'}
        onOpenFull={() => { navigate(`/work-orders/${panelWoId}`); setPanelWoId(null); }}
        theme={theme}
      >
        {!panelWo ? (
          <div style={{ paddingTop: theme.spacing.lg }}>
            <SkeletonTable rows={4} cols={2} theme={theme} />
          </div>
        ) : (
          <>
            <BadgeRow theme={theme}>
              <StatusBadge theme={theme} status={panelWo.status}>
                {getWorkOrderStatusLabel(panelWo.status)}
              </StatusBadge>
              {panelWo.priority && (
                <StatusBadge theme={theme} status={null} style={{ backgroundColor: theme.colors.border, color: theme.colors.text }}>
                  {panelWo.priority}
                </StatusBadge>
              )}
            </BadgeRow>

            <DetailSection theme={theme}>
              <DetailLabel theme={theme}>Customer</DetailLabel>
              <DetailValue theme={theme}>{getCustomerName(panelWo) || '—'}</DetailValue>
            </DetailSection>

            <DetailSection theme={theme}>
              <DetailLabel theme={theme}>Location</DetailLabel>
              <DetailValue theme={theme}>{panelWo.locationName || panelWo.location?.name || '—'}</DetailValue>
            </DetailSection>

            <DetailSection theme={theme}>
              <DetailLabel theme={theme}>Assigned To</DetailLabel>
              <DetailValue theme={theme}>{getAssignedToName(panelWo) || '—'}</DetailValue>
            </DetailSection>

            <DetailSection theme={theme}>
              <DetailLabel theme={theme}>Service Date</DetailLabel>
              <DetailValue theme={theme}>{formatDateOnly(panelWo.serviceDate) || '—'}</DetailValue>
            </DetailSection>

            <DetailSection theme={theme}>
              <DetailLabel theme={theme}>Due Date</DetailLabel>
              <DetailValue theme={theme}>{formatDateOnly(panelWo.dueDate) || '—'}</DetailValue>
            </DetailSection>

            {panelWo.description && (
              <DetailSection theme={theme}>
                <DetailLabel theme={theme}>Description</DetailLabel>
                <DetailValue theme={theme} style={{ lineHeight: 1.5, color: theme.colors.muted }}>
                  {panelWo.description.length > 300 ? panelWo.description.slice(0, 300) + '…' : panelWo.description}
                </DetailValue>
              </DetailSection>
            )}

            <Button
              onClick={() => { navigate(`/work-orders/${panelWoId}`); setPanelWoId(null); }}
              style={{ width: '100%', marginTop: theme.spacing.md }}
            >
              Open Full Details <ExternalLink size={12} />
            </Button>
          </>
        )}
      </SlidePanel>

      {canGenerateDemoData && (
        <GenerateDemoDataDialog
          entityType="workorders"
          targetId={user?.organizationId}
          entityName="Work orders"
          entityNoun="work orders"
          open={isDemoDataOpen}
          onClose={() => setIsDemoDataOpen(false)}
          onCommitted={() => { setIsDemoDataOpen(false); window.location.reload(); }}
        />
      )}
    </Layout>
  );
};

export default WorkOrdersPage;
export { WorkOrdersPage };
